<template>
    <BaseDialog :open="true" title="So funktioniert Sitzplan" @close="skip">
        <ol class="steps" aria-label="Schritte">
            <li v-for="(slide, i) in slides" :key="i">
                <button type="button" class="step" :class="{ active: i === current }" :aria-current="i === current ? 'step' : null" @click="goTo(i)">
                    <span class="step-number">{{ i + 1 }}</span> {{ slide.short }}
                </button>
            </li>
        </ol>
        <div class="slide" aria-live="polite"
             @touchstart.passive="onTouchStart"
             @touchmove.passive="onTouchMove"
             @touchend="onTouchEnd">
            <span class="icon"><component :is="slides[current].icon" aria-hidden="true" /></span>
            <div class="slide-text">
                <h3 class="leaf-title">{{ slides[current].title }}</h3>
                <p>{{ slides[current].text }}</p>
            </div>
        </div>
        <div class="actions">
            <button type="button" class="btn" v-if="current > 0" @click="current -= 1">Zurück</button>
            <button type="button" class="btn btn-primary" v-if="current < slides.length - 1" @click="next">Weiter</button>
            <button type="button" class="btn btn-primary" v-else @click="complete">Los gehts</button>
            <button type="button" class="btn btn-quiet skip" v-if="current < slides.length - 1" @click="skip">Überspringen</button>
        </div>
    </BaseDialog>
</template>

<script>
import { Dices, GraduationCap, LayoutGrid } from '@lucide/vue';
import BaseDialog from './ui/BaseDialog.vue';

export default {
    name: 'Onboarding',
    components: { BaseDialog, Dices, GraduationCap, LayoutGrid },
    emits: ['complete'],
    data() {
        return {
            current: 0,
            touchStartX: 0,
            touchDeltaX: 0,
            slides: [
                {
                    icon: 'GraduationCap',
                    short: 'Klasse',
                    title: 'Importiere oder erstelle Klassen',
                    text: 'Lade eine CSV mit Vor- und Nachnamen hoch oder lege Klassen von Hand an.'
                },
                {
                    icon: 'LayoutGrid',
                    short: 'Zimmer',
                    title: 'Erstelle deine Schulzimmer',
                    text: 'Lege deine Zimmer an und klicke die Felder an, auf denen Tische stehen.'
                },
                {
                    icon: 'Dices',
                    short: 'Mischen',
                    title: 'Durchmischen und los',
                    text: 'Wähle Klasse und Zimmer, dann mischen. Der Sitzplan bleibt gespeichert, bis du neu mischst.'
                }
            ]
        }
    },
    methods: {
        next() {
            this.current = Math.min(this.current + 1, this.slides.length - 1);
        },
        goTo(i) {
            this.current = i;
        },
        complete() {
            this.skip();
        },
        skip() {
            try {
                localStorage.setItem('onboarding_completed', '1');
            } catch (e) {
                // Without storage the tour simply shows again next time
            }
            this.$emit('complete');
        },
        onTouchStart(e) {
            this.touchStartX = e.changedTouches[0].clientX;
            this.touchDeltaX = 0;
        },
        onTouchMove(e) {
            this.touchDeltaX = e.changedTouches[0].clientX - this.touchStartX;
        },
        onTouchEnd() {
            if (this.touchDeltaX > 50) {
                this.current = Math.max(0, this.current - 1);
            } else if (this.touchDeltaX < -50) {
                this.current = Math.min(this.slides.length - 1, this.current + 1);
            }
            this.touchStartX = 0;
            this.touchDeltaX = 0;
        }
    }
}
</script>

<style scoped>
.steps {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    margin: 0;
    padding: 0;
    list-style: none;
}

.step {
    display: inline-flex;
    align-items: center;
    gap: var(--s-2);
    min-height: 36px;
    padding: 0 var(--s-3) 0 6px;
    border: var(--line-w) solid transparent;
    border-radius: var(--r-pill);
    background: transparent;
    font-size: var(--t-sm);
    font-weight: 700;
    cursor: pointer;
}

.step:hover {
    background: var(--ground-wash);
}

.step.active {
    border-color: var(--ink);
    background: var(--paper);
    box-shadow: var(--shadow-sm);
}

.step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: var(--line);
    border-radius: 50%;
    font-size: var(--t-xs);
    font-weight: 800;
}

.step.active .step-number {
    background: var(--ink);
    color: var(--paper);
}

.slide {
    display: flex;
    align-items: flex-start;
    gap: var(--s-4);
    min-height: 120px;
    padding: var(--s-4);
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--ground-wash);
}

.slide-text {
    display: grid;
    gap: var(--s-2);
}

.slide-text p {
    font-weight: 600;
    color: var(--ink-2);
}

.icon {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border: var(--line);
    border-radius: 50%;
    background: var(--hue-periwinkle);
}

.icon svg {
    width: 24px;
    height: 24px;
    stroke-width: 2.5;
}

.skip {
    margin-left: auto;
}
</style>
